import { ReactNode } from "react";

import Card from "../card";
import Typography from "../typography";
import { cn } from "../../utls/utils";

interface ThemeSwatch {
  name: string;
  description: string;
  className: string;
  borderClassName?: string;
  helper?: ReactNode;
}

const defaultSwatches: ThemeSwatch[] = [
  {
    name: "Terracotta 500",
    description: "Primary accent used for interactive elements and headers.",
    className: "bg-terracotta-500",
    borderClassName: "border-terracotta-300",
  },
  {
    name: "Beige 100",
    description: "Soft background layer for cards and secondary surfaces.",
    className: "bg-beige-100",
    borderClassName: "border-beige-300",
  },
  {
    name: "Cream 50",
    description: "Base canvas color that keeps layouts feeling airy.",
    className: "bg-cream-50",
    borderClassName: "border-cream-200",
  },
  {
    name: "Brown 400",
    description: "Neutral text tone that pairs well with Terracotta accents.",
    className: "bg-brown-400",
    borderClassName: "border-brown-500",
  },
];

export interface ThemeExampleCardProps {
  title?: string;
  subtitle?: string;
  swatches?: ThemeSwatch[];
  className?: string;
}

export function ThemeExampleCard({
  title = "Harper Theme Preview",
  subtitle = "These tokens showcase the warm Terracotta-forward palette used across Harper surfaces.",
  swatches = defaultSwatches,
  className,
}: ThemeExampleCardProps) {
  return (
    <Card
      variant="cream"
      elevation={2}
      className={cn("w-full overflow-hidden border-cream-200", className)}
    >
      <Card.Header
        variant="cream"
        divider
        className="bg-cream-100"
      >
        <div className="space-y-1">
          <Typography variant="h4" className="text-terracotta-700">
            {title}
          </Typography>
          <Typography variant="body2" className="text-brown-600">
            {subtitle}
          </Typography>
        </div>
      </Card.Header>
      <Card.Body className="space-y-6 bg-white">
        <div className="grid gap-4 sm:grid-cols-2">
          {swatches.map((swatch) => (
            <div
              key={swatch.name}
              className="flex items-center gap-4 rounded-lg border bg-cream-50 p-4 shadow-sm"
            >
              <span
                aria-hidden
                className={cn(
                  "h-12 w-12 rounded-md border",
                  swatch.className,
                  swatch.borderClassName
                )}
              />
              <div className="space-y-1">
                <Typography variant="subtitle2" className="text-terracotta-700">
                  {swatch.name}
                </Typography>
                <Typography variant="caption" className="text-brown-600">
                  {swatch.description}
                </Typography>
                {swatch.helper ? (
                  <div className="text-xs text-terracotta-600">{swatch.helper}</div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-lg border border-beige-300 bg-beige-50 p-4">
          <Typography variant="subtitle1" className="text-brown-700">
            Usage tip
          </Typography>
          <Typography variant="body2" className="text-brown-600">
            Combine Terracotta accents with neutral Cream backgrounds and Brown text
            styles to maintain hierarchy while staying on brand.
          </Typography>
        </div>
      </Card.Body>
    </Card>
  );
}

export default ThemeExampleCard;
